import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { useApp } from "../../context/AppContext";
import type { FeatureEntitlement } from "../../lib/types";

const FINANCE_GROUPS = new Set([
  "finance",
  "payments",
  "expenses",
  "accounts",
  "closures",
  "journal",
  "reports",
  "accounting",
]);

const READ_ACTIONS = new Set([
  "view",
  "filter",
  "export_csv",
  "print",
  "download",
  "cashflow",
  "receivables",
  "sessions",
]);

function isWriteFeature(feature: FeatureEntitlement) {
  const action = feature.code.split(".").at(-1) || "";
  return !READ_ACTIONS.has(action);
}

export function useFormationAccess() {
  const { bootstrap } = useApp();
  const access = bootstrap?.business_app_access?.formation;
  const membership = bootstrap?.membership;
  const features = new Map((access?.features || []).map((item) => [item.code, item]));
  const explicitPermissions = new Set(membership?.permissions || []);

  function roleAllowsWrite(feature: FeatureEntitlement) {
    if (explicitPermissions.has(feature.code)) return true;
    const role = membership?.role || "";
    if (FINANCE_GROUPS.has(feature.group)) {
      return ["OWNER", "ADMIN", "MANAGER", "ACCOUNTANT"].includes(role);
    }
    return ["OWNER", "ADMIN", "MANAGER"].includes(role);
  }

  function usable(code: string) {
    const feature = features.get(code);
    if (!feature?.available) return false;
    if (!isWriteFeature(feature)) return true;
    return roleAllowsWrite(feature);
  }

  return {
    access,
    level: access?.access_level || "STARTER",
    feature: (code: string) => features.get(code),
    included: (code: string) => Boolean(features.get(code)?.included),
    runtimeAvailable: (code: string) => Boolean(features.get(code)?.available),
    available: usable,
    canWrite: (code: string) => {
      const feature = features.get(code);
      return Boolean(feature?.available && isWriteFeature(feature) && roleAllowsWrite(feature));
    },
  };
}

export function FormationFeatureGate({
  code,
  children,
}: {
  code: string;
  children: ReactNode;
}) {
  const { available } = useFormationAccess();
  if (!available(code)) return <Navigate to="/formation" replace />;
  return <>{children}</>;
}
